import { useCallback, useEffect, useRef, useState } from 'react';

const isWindowDefined = typeof window !== 'undefined';
const DEFAULT_HIDE_TIMEOUT_SECONDS = 5;

export function useBannerVisibility(): { isVisible: boolean; handleModalConfirm: (timeoutSeconds: number) => void } {
  const [isVisible, setIsVisible] = useState(true);
  const timerIdRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerIdRef.current && isWindowDefined) {
      window.clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clearTimer();
    setIsVisible(true);
  }, [clearTimer]);

  const hide = useCallback(
    (timeoutSeconds: number) => {
      clearTimer();
      setIsVisible(false);

      if (isWindowDefined && timeoutSeconds > 0) {
        timerIdRef.current = window.setTimeout(() => {
          show();
        }, timeoutSeconds * 1000);
      }
    },
    [clearTimer, show],
  );

  const toggle = useCallback(() => {
    if (isVisible) {
      hide(DEFAULT_HIDE_TIMEOUT_SECONDS);
    } else {
      show();
    }
  }, [isVisible, hide, show]);

  const handleModalConfirm = useCallback(
    (timeoutSeconds: number) => {
      hide(timeoutSeconds);
    },
    [hide],
  );

  // Keyboard shortcut: Cmd+Ctrl+A to toggle banner
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // check if the key combination is command + ctrl + a
      if (event.metaKey && event.ctrlKey && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        toggle();
      }
    };

    // Add the event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up the event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return {
    isVisible,
    handleModalConfirm,
  };
}

export default useBannerVisibility;
